import React, { useEffect } from "react";
import { StyleSheet, ScrollView, View } from "react-native";
import { router, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { CheckboxRow } from "@/components/ui/CheckboxRow";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { useApply } from "@/contexts/ApplyContext";

const STEP_INDEX = 2;

const PHYSICAL_REQUIREMENTS = [
  "Safely lifting up to 30lbs on a regular basis",
  "Safely lifting up to 50lbs on an occasional basis",
  "Tolerating extremes in temperature and weather conditions",
  "Climbing stairs as well as fixed and portable ladders",
  "Occasionally working off of ladders, crouching, crawling and maneuvering in areas with limited space",
  "Moving heavy objects up and down ramps",
  "Reaching overhead and performing tasks that require fine motor skills",
  "Climbing on to or off of elevated work surfaces",
  "Gripping with full range of motion of the upper extremities",
  "Communicating verbally and hearing verbal communication",
  "Reading and speaking English",
];

export default function PhysicalScreen() {
  const { colors } = useTheme();
  const { formData, updateFormData, updateStep } = useApply();
  const pathname = usePathname();

  // Track current step (only when this screen is active)
  useEffect(() => {
    if (pathname.includes("/physical")) {
      updateStep(STEP_INDEX);
    }
  }, [pathname, updateStep]);

  const allChecked = formData.physicalRequirements.every(Boolean);
  const someChecked = formData.physicalRequirements.some(Boolean);

  const handleToggleItem = (index: number) => {
    const newRequirements = [...formData.physicalRequirements];
    newRequirements[index] = !newRequirements[index];
    updateFormData({ physicalRequirements: newRequirements });
  };

  const handleToggleAll = () => {
    const newValue = !allChecked;
    updateFormData({
      physicalRequirements: Array(PHYSICAL_REQUIREMENTS.length).fill(newValue),
    });
  };

  const handleContinue = () => {
    router.push("/apply/additional");
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <MaterialIcons name="fitness-center" size={40} color={colors.primary} />
          <ThemedText style={[styles.title, { color: colors.text }]}>Physical Requirements</ThemedText>
          <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
            Please confirm you can safely perform the following tasks required for this position
          </ThemedText>
        </View>

        <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={[styles.masterCheckbox, { borderBottomColor: colors.border }]}>
            <CheckboxRow
              label="I confirm I can perform all of the following requirements"
              checked={allChecked}
              onToggle={handleToggleAll}
              style={styles.masterCheckboxRow}
            />
          </View>

          <View style={styles.requirementsList}>
            {PHYSICAL_REQUIREMENTS.map((requirement, index) => (
              <CheckboxRow
                key={index}
                label={requirement}
                checked={formData.physicalRequirements[index]}
                onToggle={() => handleToggleItem(index)}
              />
            ))}
          </View>
        </View>

        <View style={[styles.infoCard, { backgroundColor: colors.sky?.background || "#E0F2FE" }]}>
          <MaterialIcons name="info" size={20} color={colors.sky?.text || "#0284C7"} />
          <ThemedText style={[styles.infoText, { color: colors.sky?.text || "#0284C7" }]}>
            These requirements are essential for the safety of yourself and others. If you have any concerns or require
            accommodations, please contact us before proceeding.
          </ThemedText>
        </View>

        <View style={styles.buttonRow}>
          <Button
            text="Back"
            icon="arrow-left"
            iconPosition="left"
            iconSize={18}
            iconColor={colors.text}
            onPress={() => router.push("/apply/info")}
            style={[styles.backButton, { borderColor: colors.border }]}
            textStyle={[styles.backButtonText, { color: colors.text }]}
          />
          <Button
            text="Continue"
            icon="arrow-right"
            iconPosition="right"
            iconSize={18}
            iconColor="white"
            onPress={handleContinue}
            enabled={allChecked}
            style={[
              styles.continueButton,
              {
                backgroundColor: allChecked ? colors.primary : colors.border,
                opacity: allChecked ? 1 : 0.5,
              },
            ]}
            textStyle={styles.continueButtonText}
          />
        </View>

        {!allChecked && someChecked && (
          <ThemedText style={[styles.warningText, { color: colors.ruby?.text || "#DC2626" }]}>
            Please confirm all requirements to continue
          </ThemedText>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    gap: 20,
  },
  header: {
    alignItems: "center",
    gap: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  masterCheckbox: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
  },
  masterCheckboxRow: {
    paddingVertical: 8,
  },
  requirementsList: {
    padding: 16,
    paddingTop: 8,
    gap: 4,
  },
  infoCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    padding: 16,
    borderRadius: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
    marginTop: 8,
  },
  backButton: {
    flex: 1,
    borderRadius: 8,
    minHeight: 52,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: "600",
  },
  continueButton: {
    flex: 2,
    borderRadius: 8,
    minHeight: 52,
  },
  continueButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  warningText: {
    fontSize: 14,
    textAlign: "center",
  },
});
